import React, {useEffect, useMemo, useState} from 'react';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import {useTranslation} from 'react-i18next';
import './CreateNewJobFlyout.scss';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {getTreeDataForType, statusOptions} from './mock';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ConfirmModal from './ConfirmModal';
import {ISelectItem} from '../../../../shared/types';
import {isNull} from 'lodash';
import {buildTree, getNodeById, transformTreeData} from './utils';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import {
  useGetFoldersList,
  useGetTagsFoldersViewData,
  useGetTagsList,
} from '../../../../hooks/tagsFoldering/useTagsFoldering';
import {RootState} from '../../../../redux/store';
import {useSelector} from 'react-redux';
import {t} from 'i18next';
import {
  useCreateNewSummarizeJob,
  useGetSourceTypes,
} from '../../../../hooks/ai/summarized/useSummarizes';
import {ToastType} from '../../../../shared/constants';
import {UseSummarizedGrid} from '../data-grid/SummarizeDataGridController';
import {useGetSavedSearchList} from '../../../../hooks/ai/investigate/useInvestigate';

interface ICreateNewJobFlyout {
  isOpen: boolean;
  onClose: () => void;
  setShowToast: (value: boolean) => void;
  triggerToast: (toastMsg: string, toastStatus: ToastType[]) => void;
}

const initialSelectedSecurityGroup: ISelectItem = {
  securityGroupID: '',
  securityGroupName: t('tagsAndFolders.allSecurityGroups'),
};

const CreateNewJobFlyout = (props: ICreateNewJobFlyout) => {
  const {isOpen, onClose, setShowToast, triggerToast} = props;
  const {t} = useTranslation();
  const [selectedSourceName, setSelectedSourceName] = useState<string>('--');
  const [documentCount, setDocumentCount] = useState<number | '--'>('--');
  const [isConfirmModalOpen, setIsconfirmModalOpen] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<ISelectItem | null>(
    null
  );
  const [selectedTreeItem, setSelectedTreeItem] = useState<string[]>([]);

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const [selectedSecurityGroup, setSelectedSecurityGroup] =
    useState<ISelectItem | null>(
      loginUserData.roleId === 1 ? initialSelectedSecurityGroup : null
    );

  const queryParams = useMemo(() => {
    const toggleValue = 1;

    const securityroupId = selectedSecurityGroup?.securityGroupID || 0;

    return `?securityGroupId=${securityroupId}&toggelValue=${toggleValue}`;
  }, [selectedSecurityGroup?.securityGroupID]);

  const shouldFetchTags =
    selectedSource?.name === 'Tags' && !!selectedSecurityGroup;

  const shouldFetchFolders =
    selectedSource?.name === 'Folders' && !!selectedSecurityGroup;

  const {refetch} = UseSummarizedGrid();

  const {
    data: foldersList,
    isLoading: isLoadingFoldersList,
    refetch: refetchGetFolderList,
  } = useGetFoldersList(queryParams, shouldFetchFolders);
  const {
    data: tagsList,
    isLoading: isLoadingTagsList,
    refetch: refetchGetTagList,
  } = useGetTagsList(queryParams, shouldFetchTags);

  const {data: viewData, isLoading: isLoadingViewData} =
    useGetTagsFoldersViewData();

  const {data: getSourceTypes} = useGetSourceTypes('Summarize');

  const {mutate: createNewSummarizeJob} = useCreateNewSummarizeJob();

  const {
    mutate: fetchSavedSearchList,
    data: savedSearchList = [],
    isPending: isLoadingSavedSearchList,
  } = useGetSavedSearchList();

  const getTreeDataList =
    useMemo((): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
      switch (selectedSource?.name) {
        case 'Tags':
          return buildTree(tagsList?.tagList ?? []);

        case 'Folders':
          return buildTree(foldersList ?? []);

        case 'Saved Search':
          return buildTree(savedSearchList ?? []);

        default:
          return [];
      }
    }, [selectedSource?.name, tagsList?.tagList, foldersList, savedSearchList]);

  const securityGroupList = useMemo(() => {
    const list = viewData?.securityGroupList || [];
    return loginUserData.roleId === 1
      ? [initialSelectedSecurityGroup, ...list]
      : list;
  }, [viewData, loginUserData.roleId]);

  const sourceTypeOptions: ISelectItem[] = useMemo(() => {
    const sourceTypeMap: Record<string, string> = {
      Folder: 'Folders',
      Savedsearch: 'Saved Search',
      Tag: 'Tags',
    };

    return (getSourceTypes || []).map(({sourceType}: {sourceType: string}) => {
      const value = sourceTypeMap[sourceType] ?? sourceType;

      return {
        id: value,
        name: value,
        label: value,
      };
    });
  }, [getSourceTypes]);

  const treeData = useMemo(() => {
    return getTreeDataForType(selectedSource?.name, getTreeDataList);
  }, [selectedSource?.name, getTreeDataList]);

  useEffect(() => {
    if (loginUserData.roleId !== 1 && securityGroupList.length > 0) {
      setSelectedSecurityGroup(securityGroupList[0] as ISelectItem);
    }
  }, [loginUserData.roleId, securityGroupList]);

  const handleCreateNewSummarizeJob = () => {
    const sourceType = (sourceName: string | number | undefined) => {
      switch (sourceName) {
        case 'Tags':
          return 'Tag';
        case 'Folders':
          return 'Folder';
        case 'Saved Search':
          return 'SavedSearch';
        default:
          return '';
      }
    };
    const payload = {
      sourceType: sourceType(selectedSource?.name),
      sourceIds: selectedTreeItem?.map((i: string) => Number(i)),
    };

    createNewSummarizeJob(payload, {
      onSuccess: (data) => {
        setIsconfirmModalOpen(false);
        onClose();
        triggerToast(t('ai.summarize.createJob.successMessage'), [
          ToastType.Success,
        ]);
        refetch();
      },
      onError: (error) => {
        setIsconfirmModalOpen(false);
        triggerToast(error.message, [ToastType.Error]);
      },
    });
  };

  const handleSearchKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setSearchKeyword((searchInput ?? '').trim());
    }
  };
  const isParentNode = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    return (
      node.isGroup || (Array.isArray(node.children) && node.children.length > 0)
    );
  };
  const handleSubmit = () => {
    setIsconfirmModalOpen(true);
  };

  const handleDropdownChange = (value: ISelectItem | null) => {
    setSelectedSource(value);
    setDocumentCount('--');
    setSelectedSourceName('--');
    setSelectedTreeItem([]);
    if (value?.label === 'Saved Search') {
      fetchSavedSearchList(true);
    }
  };

  const handleModalClose = () => {
    setIsconfirmModalOpen(false);
    // onClose();
  };
  function handleTreeSelection(value: string[] | null): void {
    setSelectedTreeItem(value);
    const tree = getTreeDataForType(selectedSource?.name, getTreeDataList);
    const node = value ? getNodeById(tree, value[0]) : null;
    setSelectedSourceName(node && !isParentNode(node) ? node.label : '--');
    setDocumentCount(node && !isParentNode(node) ? node.documentCount : '--');
  }

  const isSearchDisabled = isNull(selectedSource) || treeData.length === 0;

  const transformedTreeData = useMemo(() => {
    return transformTreeData(treeData);
  }, [treeData]);

  const filterTreeData = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    keyword: string
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    if (!normalizedKeyword) {
      return nodes;
    }
    return nodes
      .map((node) => {
        const children = Array.isArray(node.children)
          ? filterTreeData(
              node.children as TreeViewBaseItem<ExtendedTreeItemProps>[],
              normalizedKeyword
            )
          : [];
        const isNodeMatched = node.label
          ?.toString()
          .toLowerCase()
          .includes(normalizedKeyword);
        if (isNodeMatched || children.length > 0) {
          return {
            ...node,
            children,
          };
        }
        return null;
      })
      .filter(Boolean) as TreeViewBaseItem<ExtendedTreeItemProps>[];
  };

  const filteredTreeData = useMemo(() => {
    return filterTreeData(transformedTreeData, searchKeyword);
  }, [transformedTreeData, searchKeyword]);

  return (
    <div
      className="create-new-summarize-job"
      data-testid={`create-new-job-flyout-`}
    >
      <ArrDrawer
        isOpen={isOpen}
        showButton={false}
        showHeader={false}
        onClose={onClose}
        side={'right'}
        size={'small'}
        className="create-new-summarize-job"
      >
        <Box className="ai-summarize-flyout">
          <Box className="flyout_header">
            <Box className="title-box">
              <Typography
                tabIndex={0}
                className="typo-text headline3_size title"
                data-testid="flyout-title"
              >
                {t('ai.summarize.createJob.title')}
              </Typography>
            </Box>

            <Box className="action-buttons-box">
              <ArrButton
                variant="secondary"
                size="medium"
                onClick={onClose}
                aria-label={t('common.cancel')}
                dataTestId="create-new-job-flyout-cancel-button"
              >
                {t('common.cancel')}
              </ArrButton>

              <ArrButton
                variant="primary"
                size="medium"
                type="submit"
                aria-label={t('common.submit')}
                dataTestId="create-new-job-flyout-submit-button"
                onClick={handleSubmit}
                isAriaDisabled={selectedSourceName === '--'}
              >
                {t('common.submit')}
              </ArrButton>
            </Box>
          </Box>

          <Box className="content">
            <Box className="sub-content">
              <Typography
                variant="subtitle2"
                tabIndex={0}
                className="typo-text subhead4_size text-color"
                data-testid="arr-dropdown-header"
              >
                {t('ai.summarize.createJob.selectDocuments')}
              </Typography>

              <ArrTooltipWrapper
                data-testid="select-document-tooltip-wrapper"
                trigger={
                  <InfoIcon
                    tabIndex={0}
                    className="info-icon"
                    data-testid="info-icon"
                  />
                }
              >
                {t('ai.summarize.createJob.tooltip')}
              </ArrTooltipWrapper>
            </Box>

            <Box className="sub-content">
              <Typography
                tabIndex={0}
                className="typo-text body3_size text-color"
                data-testid={`create-new-job-flyout-select-source-instruction`}
              >
                {t('ai.summarize.createJob.selectSourceInstruction')}
              </Typography>
            </Box>

            <Box className="dropdown">
              <ArrSingleSelectDropdown
                sizeVariant="medium"
                selectedValues={selectedSource}
                onSelectionChange={handleDropdownChange}
                dropdownLabel={t('ai.summarize.createJob.dropdownLabel')}
                dropdownItems={
                  sourceTypeOptions.length ? sourceTypeOptions : statusOptions
                }
                customWidth={17}
                dataTestId="asd-create-job-dropdown-label"
                tabIndex={1}
              />
            </Box>

            <Box className="sub-content">
              <Typography
                tabIndex={0}
                className="typo-text body3_size sub-content-key"
                data-testid={`create-new-job-flyout-selected-source`}
              >
                {t('ai.summarize.createJob.selectedSource')}{' '}
                <span
                  className="typo-text label2_size sub-content-value"
                  data-testid={`create-new-job-flyout-source-name`}
                >
                  {selectedSourceName}
                </span>
              </Typography>
            </Box>

            <Box className="sub-content">
              <Typography
                tabIndex={0}
                className="typo-text body3_size sub-content-key"
                data-testid={`create-new-job-flyout-document-count`}
              >
                {t('ai.summarize.createJob.documentCount')}{' '}
                <span
                  className="typo-text label2_size sub-content-value"
                  data-testid={`create-new-job-flyout-doc-count`}
                >
                  {documentCount}
                </span>
              </Typography>
            </Box>

            <ArrTextField
              state={isSearchDisabled ? 'inactive' : 'enabled'}
              isAriaDisabled={isSearchDisabled}
              sizeVariant="medium"
              className="search-input"
              placeholder={t('batchPrint.sourceSelection.searchPlaceholder')}
              aria-label={t('batchPrint.sourceSelection.searchPlaceholder')}
              dataTestId="search-input-new-job-flyout"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (e.target.value.trim() === '') {
                  setSearchKeyword('');
                }
              }}
              onKeyDown={handleSearchKeyEnter}
              onClear={() => {
                setSearchInput('');
                setSearchKeyword('');
              }}
              type="search"
              disabled={true}
            />
          </Box>

          {!isNull(selectedSource) && (
            <Box className="tree-view">
              {filteredTreeData.length === 0 ? (
                <div
                  className="empty"
                  data-testid="create-new-job-no-search-results"
                >
                  {t('common.messageForNoGridData')}
                </div>
              ) : (
                <ArrTreeView
                  selectedItem={selectedTreeItem}
                  setSelectedItem={handleTreeSelection}
                  itemsList={filteredTreeData}
                  isShowCheckbox={true}
                  searchKeyword={searchKeyword}
                  expandOnSearch={true}
                  showTooltip
                  readActiveCheckboxAccessibilityProps={true}
                  dataTestId="create-new-job-tree"
                />
              )}
            </Box>
          )}
        </Box>

        {isConfirmModalOpen && (
          <ConfirmModal
            isOpen={isConfirmModalOpen}
            onClose={handleModalClose}
            selectedDocumentCount={documentCount}
            setShowToast={setShowToast}
            handleSubmit={handleCreateNewSummarizeJob}
          />
        )}
      </ArrDrawer>
    </div>
  );
};

export default CreateNewJobFlyout;
